import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import { Button, CircularButton, Icon, icons } from '@hoffmann/pattern-library-2';
import { useFlag } from '@unleash/proxy-client-react';

import { useDocumentTitleV2 } from '../../hooks/browser/useDocumentTitle';
import { useGoogleAnalytics } from '../../hooks/tracking/useGoogleAnalytics/useGoogleAnalytics';
import { useGoogleTagManager } from '../../hooks/tracking/useGoogleTagManager/useGoogleTagManager';
import { withStyledClasses } from '../../styled/withStyledClasses';
import { templates } from '../../templates/templates';
import { getImageUrl } from '../../utils/assetsUrlBuilder';

export const LandingPage: React.FC = () => {
  /* libs hooks */
  const history = useNavigate();
  const t = useTranslate('LandingPage');
  /* app hooks */
  const isTagManagerEnabled = useFlag('wsc.use-tag-manager');
  const { gaPageView } = useGoogleAnalytics(!isTagManagerEnabled);
  const { gaPageView: gaPageViewTM } = useGoogleTagManager(isTagManagerEnabled);
  const pageTitle = useDocumentTitleV2('LANDING_PAGE');

  /* local states */
  const [selectedTemplate, setSelectedTemplate] = useState<number>(0);

  /* send event to GA */
  useEffect(() => {
    if (pageTitle) {
      isTagManagerEnabled ? gaPageViewTM(pageTitle) : gaPageView(pageTitle);
    }
  }, [pageTitle, gaPageView, gaPageViewTM, isTagManagerEnabled]);

  const handleStartWithTemplateClick = () => {
    history(`/assemble/template-${selectedTemplate}`);
  };

  return (
    <Page>
      <Section>
        <Image
          style={{
            backgroundImage: `url("${getImageUrl('920012 1500')}")`,
          }}
        />
        <Description>
          <Text>{t('CONFIGURE_WORKSPACE')}</Text>
          <Button
            label={t('START_NEW_CONFIGURATION')}
            onClick={() => {
              // throw new Error('test sentry errors');
              history('/select-workstation');
            }}
            theme="brand"
          />
          {templates.length > 1 && <Separator />}
        </Description>
      </Section>
      <Section>
        <Image
          style={{
            backgroundImage: `url("assets/templates/${templates[selectedTemplate].image}")`,
          }}
        >
          {templates.length > 1 && (
            <>
              <LeftArrow
                icon={<Icon source={icons.ArrowBack} />}
                size={200}
                ghost
                onClick={() => setSelectedTemplate(mod(selectedTemplate - 1, templates.length))}
              />
              <RightArrow
                icon={<Icon source={icons.ArrowForward} />}
                size={200}
                ghost
                onClick={() => setSelectedTemplate(mod(selectedTemplate + 1, templates.length))}
              />
            </>
          )}
        </Image>
        <Description>
          <Text>{t('CONFIGURE_TEMPLATE')}</Text>
          <Button
            label={t('START_WITH_TEMPLATE')}
            onClick={handleStartWithTemplateClick}
            theme="brand"
          />
          {templates.length > 1 && (
            <Carousel>
              {templates.map((_, index) => (
                <Circle
                  key={`carousel_item_${index}`}
                  onClick={() => setSelectedTemplate(index)}
                  selected={index === selectedTemplate}
                />
              ))}
            </Carousel>
          )}
        </Description>
      </Section>
    </Page>
  );
};
const Page = styled.div`
  display: flex;
  flex: 1;
  align-items: center;

  & > * {
    max-width: 50%;
  }
`;

const Section = styled.div`
  ${({ theme }) => `
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    background-color: ${theme.colors.customGrey4};

    &:hover {
      background-color: white;
    }
  `}
`;

const Image = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 80%;
  flex: 3 0 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
`;

const Text = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
`;

const LeftArrow = styled(withStyledClasses(CircularButton))`
  position: absolute;
  left: -32px;
  font-size: 3rem;
`;

const RightArrow = styled(withStyledClasses(CircularButton))`
  position: absolute;
  right: -32px;
  font-size: 3rem;
`;

const Carousel = styled.div`
  display: flex;
  margin-top: 32px;

  * + * {
    margin-left: 8px;
  }
`;

const Circle = styled.div<{ selected: boolean }>`
  ${({ theme, selected }) => `
  border-radius: 16px;
  background-color: ${selected ? theme.colors.layout90 : theme.colors.layout60};
  width: 8px;
  height: 8px;

  &:hover {
      cursor: pointer;
    }
  `}
`;

const Separator = styled.div`
  height: 8px;
  margin-top: 32px;
`;

const mod = (n: number, m: number) => ((n % m) + m) % m;
